import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { useAuth } from "./hooks/useAuth";
import MainLayout from "./components/layout/MainLayout";
import LoginPage from "./pages/auth/LoginPage";
import DramaListPage from "./pages/videos/DramaListPage";
import DramaDetailPage from "./pages/videos/DramaDetailPage";
import DramaEditPage from "./pages/videos/DramaEditPage";
import DramaPricingPage from "./pages/videos/DramaPricingPage";
import UserListPage from "./pages/users/UserListPage";
import UserDetailPage from "./pages/users/UserDetailPage";
import AnnouncementListPage from "./pages/announcements/AnnouncementListPage";
import AnnouncementEditPage from "./pages/announcements/AnnouncementEditPage";
import CommentListPage from "./pages/moderation/CommentListPage";
import DmModerationPage from "./pages/moderation/DmModerationPage";
import ArtistModerationPage from "./pages/moderation/ArtistModerationPage";
import CreatorApplicationsPage from "./pages/creators/CreatorApplicationsPage";
import FanPassRequestsPage from "./pages/creators/FanPassRequestsPage";
import TransactionListPage from "./pages/financial/TransactionListPage";
import FailedPurchaseListPage from "./pages/financial/FailedPurchaseListPage";
import SettingsPage from "./pages/settings/SettingsPage";
import IAMPage from "./pages/iam/IAMPage";
import TagListPage from "./pages/tags/TagListPage";
import TagEditPage from "./pages/tags/TagEditPage";
import GiftCodeListPage from "./pages/giftCodes/GiftCodeListPage";
import GiftCodeEditPage from "./pages/giftCodes/GiftCodeEditPage";
import GiftCodeDetailPage from "./pages/giftCodes/GiftCodeDetailPage";
import HomeLayoutPage from "./pages/homeLayout/HomeLayoutPage";
import HomeLayoutEditPage from "./pages/homeLayout/HomeLayoutEditPage";
import ActorListPage from "./pages/actors/ActorListPage";
import ActorEditPage from "./pages/actors/ActorEditPage";
import FeaturedArtistsPage from "./pages/actors/FeaturedArtistsPage";
import ContentReviewPage from "./pages/creators/ContentReviewPage";
import { artistHubEnabled } from "./services/firebase/artistHub";
import ProductListPage from "./pages/products/ProductListPage";
import ProductEditPage from "./pages/products/ProductEditPage";
import CompanyListPage from "./pages/companies/CompanyListPage";
import CompanyEditPage from "./pages/companies/CompanyEditPage";
import CompanyDetailPage from "./pages/companies/CompanyDetailPage";
import FeedbackListPage from "./pages/feedbacks/FeedbackListPage";
import FeedbackDetailPage from "./pages/feedbacks/FeedbackDetailPage";
import RevenueDashboard from "./pages/financial/RevenueDashboard";
import CompanyRevenuePage from "./pages/financial/CompanyRevenuePage";
import DramaRevenuePage from "./pages/financial/DramaRevenuePage";
import DramaPerformancePage from "./pages/financial/DramaPerformancePage";
import CompanyPerformancePage from "./pages/financial/CompanyPerformancePage";
import RevenueConfigPage from "./pages/financial/RevenueConfigPage";
import FinancialHubLayout from "./pages/financial/FinancialHubLayout";
import FanPassReportPage from "./pages/financial/FanPassReportPage";
import ContentHubLayout from "./pages/content/ContentHubLayout";

// 建立 React Query client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

function App() {
  const { user, loading, signOut } = useAuth();

  // 檢查用戶權限並自動登出沒有權限的用戶
  React.useEffect(() => {
    if (!loading && user && !user.isAdmin && !user.isStaff && !user.isDeveloper && !user.isObserver) {
      // 用戶沒有權限，自動登出並導回登入頁面
      signOut().catch(console.error);
    }
  }, [user, loading, signOut]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-surface-600 dark:text-surface-400">載入中...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <LoginPage />;
  }

  // 檢查權限：如果用戶存在但沒有權限，顯示錯誤訊息（等待登出完成）
  if (!user.isAdmin && !user.isStaff && !user.isDeveloper && !user.isObserver) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="mb-4">
            <svg className="w-16 h-16 text-red-500 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <p className="text-lg font-semibold text-red-600 dark:text-red-400 mb-2">您沒有權限</p>
          <p className="text-sm text-surface-600 dark:text-surface-400">正在登出...</p>
        </div>
      </div>
    );
  }

  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter
        future={{
          v7_startTransition: true,
          v7_relativeSplatPath: true,
        }}
      >
        <MainLayout>
          <Routes>
            <Route path="/" element={<Navigate to="/dramas" replace />} />
            {/* 內容 hub:劇集 + 標籤 + 首頁排版共用 layout,detail/edit 頁不在 hub 內 */}
            <Route element={<ContentHubLayout />}>
              <Route path="/dramas" element={<DramaListPage />} />
              <Route path="/tags" element={<TagListPage />} />
              <Route path="/home-layout" element={<HomeLayoutPage />} />
            </Route>
            <Route path="/dramas/new" element={<DramaEditPage />} />
            <Route path="/dramas/:id" element={<DramaDetailPage />} />
            <Route path="/dramas/:id/edit" element={<DramaEditPage />} />
            <Route path="/dramas/:id/pricing" element={<DramaPricingPage />} />
            <Route path="/tags/new" element={<TagEditPage />} />
            <Route path="/tags/:id/edit" element={<TagEditPage />} />
            <Route path="/users" element={<UserListPage />} />
            <Route path="/users/:id" element={<UserDetailPage />} />
            <Route path="/announcements" element={<AnnouncementListPage />} />
            <Route path="/announcements/new" element={<AnnouncementEditPage />} />
            <Route path="/announcements/:id/edit" element={<AnnouncementEditPage />} />
            <Route path="/moderation/comments" element={<CommentListPage />} />
            <Route path="/moderation/dm" element={<DmModerationPage />} />
            <Route path="/moderation/artist" element={<ArtistModerationPage />} />
            <Route path="/creators/applications" element={<CreatorApplicationsPage />} />
            <Route path="/creators/fan-pass-requests" element={<FanPassRequestsPage />} />
            {/* 財務 hub：5 個主要 tab 共用 layout，detail 頁面獨立路由 */}
            <Route path="/financial" element={<FinancialHubLayout />}>
              <Route index element={<Navigate to="/financial/revenue" replace />} />
              <Route path="revenue" element={<RevenueDashboard />} />
              <Route path="dramas" element={<DramaPerformancePage />} />
              <Route path="companies" element={<CompanyPerformancePage />} />
              <Route path="transactions" element={<TransactionListPage />} />
              <Route path="failed-purchases" element={<FailedPurchaseListPage />} />
              <Route path="revenue-configs" element={<RevenueConfigPage />} />
              <Route path="fan-pass" element={<FanPassReportPage />} />
            </Route>
            {/* Detail / drill-down 頁面：不在 tab layout 內 */}
            <Route path="/financial/revenue/company/:companyId" element={<CompanyRevenuePage />} />
            <Route path="/financial/revenue/drama/:dramaId" element={<DramaRevenuePage />} />
            <Route path="/settings" element={<SettingsPage />} />
            <Route path="/iam" element={<IAMPage />} />
            <Route path="/gift-codes" element={<GiftCodeListPage />} />
            <Route path="/gift-codes/new" element={<GiftCodeEditPage />} />
            <Route path="/gift-codes/:id/edit" element={<GiftCodeEditPage />} />
            <Route path="/gift-codes/:id" element={<GiftCodeDetailPage />} />
            <Route path="/home-layout/new" element={<HomeLayoutEditPage />} />
            <Route path="/home-layout/:id/edit" element={<HomeLayoutEditPage />} />
            <Route path="/actors" element={<ActorListPage />} />
            <Route path="/actors/new" element={<ActorEditPage />} />
            <Route path="/actors/:id/edit" element={<ActorEditPage />} />
            {artistHubEnabled && <Route path="/artists/featured" element={<FeaturedArtistsPage />} />}
            {artistHubEnabled && <Route path="/artists/review" element={<ContentReviewPage />} />}
            <Route path="/products" element={<ProductListPage />} />
            <Route path="/products/new" element={<ProductEditPage />} />
            <Route path="/products/:id/edit" element={<ProductEditPage />} />
            <Route path="/companies" element={<CompanyListPage />} />
            <Route path="/companies/new" element={<CompanyEditPage />} />
            <Route path="/companies/:id/edit" element={<CompanyEditPage />} />
            <Route path="/companies/:id" element={<CompanyDetailPage />} />
            <Route path="/feedbacks" element={<FeedbackListPage />} />
            <Route path="/feedbacks/:id" element={<FeedbackDetailPage />} />
            <Route path="*" element={<Navigate to="/dramas" replace />} />
          </Routes>
        </MainLayout>
      </BrowserRouter>
    </QueryClientProvider>
  );
}

export default App;

